/* eslint-disable @stylistic/max-len */
import { BankReviewQueue, SearchDateTypeId, UnreconciledFundsStatusId } from 'app/api/apiClient/generated/types';
import { getViewPreference } from 'app/api/cookie';
import {
  formatParamDate,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getThirtyDaysAgo,
  getThirtyDaysFromNow,
} from 'app/utils/lib/dates';

import { CampaignRelationshipStatusId } from './campaignRelationships';
import { exposureLimit } from './credit-limit-exposure';
import { defaultDates } from './dates';
import { HighwayWatchListStatusId } from './highway';
import { metricAggregateMinutesInPast } from './metrics';
import { NoaStatusId } from './noa';
import { PromotionalCampaignStatusId } from './promotionalCampaigns';
import { SettlementStatementType } from './reports';
import { SelectCarrierStatusId } from './selectCarrier';
import { emptyUuid } from './uuid';

export const externalRoutes = {
  admin: 'InternalReport',
  audit: 'InternalReport/AuditGuid',
  invoiceReport: 'SysAdminInvoice/InvoiceReport',
  misdirectedCash: 'InternalReport/MisdirectedCashView',
  misdirectedPayment: 'InternalReport/MisdirectedPaymentView',
  missingLorReport: 'SysAdminNoa/MissingLORReport',
  noaDenyList: 'SysAdminNoaDenyList',
  operationsApproval: 'SysAdminExport',
  setupFactor: 'SysAdminFactor/Edit',
  transactionView: 'InternalReport/TransactionView',
} as const;

export const routes = {
  account: 'Account',
  admin: 'admin',
  auth: 'auth',
  displayAllBrokers: 'all_brokers',
  factor: 'factor',
  factorLandingPage: 'take_control',
  getMobileApp: 'get_mobile_app',
  inactive: 'inactive',
  integrations: 'integrations',
  linkLoadPayAccount: 'link_loadpay',
  login: 'Login',
  missingDetails: 'missing_details',
  oAuth: 'Account/OAuth',
  oktaCallback: 'okta/callback',
  oktaError: 'okta/error',
  oktaExternal: 'okta/external',
  oktaLogin: 'okta/login',
  oktaSso: 'okta/sso',
  payee: 'payee',
  payor: 'payor',
  refreshToken: 'Account/refresh_token',
  registerMfa: 'register_mfa',
  registerPayee: 'register/payee',
  setPassword: 'set_password',
  triumph: 'triumph',
};

export const adminRoutes = {
  adminActions: 'admin_actions',
  advances: 'advances',
  audit: 'audit',
  bankAccountReviews: 'bank_account_reviews',
  brokerAccountMatchings: 'broker_account_matchings',
  brokerCarrierMatchings: 'broker_carrier_matchings',
  carrierActions: 'carrier_actions',
  companyDetails: 'company_details',
  creditLimitExceeded: 'credit_limit_exceeded',
  creditLimitExposure: 'credit_limit_exposure',
  csrDashboard: 'csr_dashboard',
  dashboard: 'dashboard',
  factorAssignment: 'factor_assignment',
  factorClientMatchings: 'factor_client_matchings',
  factorDebtorMatchings: 'factor_debtor_matchings',
  invoicePayDateChangeReport: 'invoice_pay_date_change_report',
  invoices: 'invoices',
  metrics: 'metrics',
  metricsHistory: 'metrics/history',
  misdirectedPayments: 'misdirected_payments',
  noa: 'noa',
  noaActionConflicts: 'noa/action_conflicts',
  noaActionConflictsHistory: 'noa/action_conflicts/history',
  noaBulkRemove: 'noa/bulk_remove',
  noaCoachableReport: 'noa/coachable_report',
  noaMetrics: 'noa/metrics',
  noaPendingFactors: 'noa/pending_factors',
  noaPendingLorDesignation: 'noa/pending_lor_designation',
  noaProcess: 'noa/process',
  noaSearch: 'noa/search',
  noaSplitQueue: 'noa/split_noas',
  noaSuccess: 'noa/success',
  payors: 'payors',
  promotionalCampaigns: 'promotional_campaigns',
  reports: 'reports',
  selectCarriers: 'select_carriers',
  serverSettings: 'server_settings',
  setup: 'setup',
  triumphLoadAdvances: 'triumph_load_advances',
  unreconciledFunds: 'unreconciled_funds',
};

export const payorRoutes = {
  billingReport: 'billing_report',
  creditLimitReport: 'credit_limit_report',
  creditNotes: 'credit_notes',
  dashboard: 'dashboard',
  gotpFees: 'gotp_fees',
  highwayAlerts: 'highway_alerts',
  holdsReport: 'holds_report',
  invoiceRefundsReport: 'invoice_refunds_report',
  invoiceReview: 'invoice_review',
  invoices: 'invoices',
  loads: 'loads',
  payments: 'payments',
  previousSettlements: 'previous_settlements',
  previousWeeklySettlements: 'previous_weekly_settlements',
  profile: 'profile',
  relationships: 'relationships',
  revenueShare: 'revenue_share',
  settlements: 'settlements',
  settlementsTerms: 'settlements_terms',
  unreconciledFundsReport: 'unreconciled_funds_report',
  users: 'users',
};

export const payeeRoutes = {
  brokers: 'brokers',
  creditNotes: 'credit_notes',
  dashboard: 'dashboard',
  invoices: 'invoices',
  invoicesSuccess: 'invoices_success',
  paperwork: 'paperwork',
  payments: 'payments',
  profile: 'profile',
  relationships: 'relationships',
  relationshipsTermsOfService: 'terms',
  selectCarrier: 'select_carrier',
  unclaimedRelationships: 'unclaimed_relationships',
  users: 'users',
};

export const factorRoutes = {
  invoices: 'invoices',
  loads: 'loads',
  payeeRelationships: 'payee_relationships',
  payments: 'payments',
  payors: 'payors',
  profile: 'profile',
  users: 'users',
};

export const defaultAdminQueryParams = {
  advancePayor: `?dateTypeId=${SearchDateTypeId.DateAdvanceScheduled}`,
  advances: `?dateTypeId=${SearchDateTypeId.DateAdvanceScheduled}`,
  bulkRemove: '',
  campaignRelationships: `?statusId=${CampaignRelationshipStatusId.Active}`,
  companyDetails: '?page=0',
  creditLimitExceeded: '',
  creditLimitExposure: `?beginDate=${defaultDates.creditLimitExceeded.beginDate}&endDate=${defaultDates.creditLimitExceeded.endDate}&exposureLimit=${exposureLimit}`,
  csrDashboard: '',
  csrInvoices: '',
  csrLoads: '',
  dashboard: '',
  factorAssignment: '',
  factorAssignmentAudit: '',
  factorAssignmentHistory: '',
  invoicePayDateChangeReport: `?startDate=${defaultDates.invoicePayDateChangeReport.startDate}&endDate=${defaultDates.invoicePayDateChangeReport.endDate}`,
  invoices: `?fromDate=${defaultDates.invoices.fromDate}&toDate=${defaultDates.invoices.toDate}&dateTypeId=${SearchDateTypeId.DateUploaded}`,
  metricsAggregateReport: `?minutesInPast=${metricAggregateMinutesInPast}`,
  misdirectedPayments: `?fromDate=${defaultDates.misdirectedPayments.fromDate}&toDate=${defaultDates.misdirectedPayments.toDate}&shouldHideCollectedFunds=true`,
  noaActionConflictsHistoryList: '?page=0&pageSize=24',
  noaActionConflictsList: `?noaRequestStatusId=${NoaStatusId.ActionConflict}&page=0&pageSize=24`,
  noaCoachableReport: `?fromDate=${defaultDates.noaCoachableReport.fromDate}&toDate=${defaultDates.noaCoachableReport.toDate}&page=0&pageSize=24`,
  noaList: '?page=0&pageSize=24',
  noaPendingFactorsList: `?noaRequestStatusId=${NoaStatusId.PendingNewFactor}&page=0&pageSize=24`,
  noaSearchList: `?fromDate=${defaultDates.noaSearchList.fromDate}&toDate=${defaultDates.noaSearchList.toDate}&page=0&pageSize=24`,
  noaSplitList: '?page=0&pageSize=24',
  payorList: '?includeInactive=false',
  pendingCarrierBanks: `?queue=${BankReviewQueue.LowRisk}&assignedTo=${emptyUuid}`,
  previousBankChanges: '',
  promotionalCampaigns: `?statusId=${PromotionalCampaignStatusId.Active}`,
  readyToProcessNoaList: `?noaRequestStatusId=${NoaStatusId.ReadyToProcess}&page=0&pageSize=24`,
  selectCarriers: `?statusId=${SelectCarrierStatusId.New}`,
  unreconciledFundsPayorShow: `?statusId=${UnreconciledFundsStatusId.Unreconciled}`,
  unreconciledFundsPayors: '',
};

export const defaultFactorQueryParams = {
  allInvoices: `?tabId=all_invoices&dateTypeId=${SearchDateTypeId.DatePaid}&page=0&pageSize=24`,
  allLoads: '?tabId=all_loads&page=0&pageSize=24',
  invoices: `?dateTypeId=${SearchDateTypeId.DatePaid}&tabId=my_invoices&page=0&pageSize=24`,
  // SearchDateTypeId.DatePaid maps to delivered date for loads
  loads: `?dateTypeId=${SearchDateTypeId.DatePaid}&tabId=my_loads&page=0&pageSize=24`,
  payeeRelationships: '?page=0&pageSize=24',
  payments: '?page=0&pageSize=24',
  payors: '?page=0&pageSize=24',
};

export const defaultPayeeQueryParams = {
  bills: `?page=0&pageSize=24&view=${getViewPreference('payeePaperworkView')}`,
  invoicesAndCreditNotes: `?dateTypeId=${SearchDateTypeId.DatePaid}&sortAsc=false&sortField=paymentDate&view=${getViewPreference('payeeInvoicesView')}`,
  payments: `?sortAsc=false&sortField=paidDate&view=${getViewPreference('payeePaymentsView')}`,
};

export const defaultPayorQueryParams = {
  billingReport: `?endDate=${formatParamDate(getThirtyDaysFromNow())}&startDate=${formatParamDate(getThirtyDaysAgo())}`,
  creditLimitReport: `?endDate=${formatParamDate(getLastDayOfMonth())}&startDate=${formatParamDate(getFirstDayOfMonth())}`,
  credits: `?view=${getViewPreference('payorCreditNotesView')}`,
  gotpFeesReport: '',
  heldInvoices: '', // kyle, fix this '?sortAsc=false&sortField=scheduledPaymentDate',
  highwayWatchlist: `?statusId=${HighwayWatchListStatusId.ActiveAlert}`,
  invoiceRefundsReport: `?endDate=${formatParamDate(getLastDayOfMonth())}&startDate=${formatParamDate(getFirstDayOfMonth())}`,
  invoiceReview: `?endDate=${defaultDates.payorInvoiceBatchReview.endDate}&startDate=${defaultDates.payorInvoiceBatchReview.startDate}`,
  invoices: `?dateTypeId=${SearchDateTypeId.DateUploaded}&view=${getViewPreference('payorInvoicesView')}`,
  loads: `?view=${getViewPreference('payorLoadsView')}`,
  payments: `?sortAsc=false&sortField=paidDate&view=${getViewPreference('payorPaymentsView')}`,
  previousSettlements: `?settlementStatementTypeId=${SettlementStatementType.Legacy}&settlementStatementTypeId=${SettlementStatementType.MonthlyStatement}&settlementStatementTypeId=${SettlementStatementType.RefundStatement}&settlementStatementTypeId=${SettlementStatementType.MonthlyStatementV2}`,
  previousWeeklySettlements: `?settlementStatementTypeId=${SettlementStatementType.WeeklyStatement}`,
  relationships: `?view=${getViewPreference('payorRelationshipsView')}`,
  revenueShare: `?endDate=${formatParamDate(getLastDayOfMonth())}&startDate=${formatParamDate(getFirstDayOfMonth())}`,
  settlements: `?sortAsc=false&sortField=submittedAt&view=${getViewPreference('payorPaperworkView')}`,
  unreconciledFunds: '',
};

export const generateTitle = (pageTitle?: string) => {
  const siteTitle = 'Triumph Payments';
  if (pageTitle) {
    return `${pageTitle} - ${siteTitle}`;
  }

  return siteTitle;
};
